import { useState, useEffect, useCallback } from 'react';
import { useAuth } from './useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Tables } from '@/integrations/supabase/types';

type UserPreferences = Tables<'user_preferences'>;

interface ExtendedUserPreferences extends UserPreferences {
  [key: string]: any;
  learning_style?: 'visual' | 'reading' | 'interactive' | 'mixed';
  preferred_difficulty?: number;
  study_time_availability?: number;
  weekly_study_goal?: number;
  onboarding_completed?: boolean;
}

// DEBUGGING HELPER - Add to window for console access
if (typeof window !== 'undefined') {
  (window as any).debugOnboarding = {
    manuallyCompleteOnboarding: () => {
      console.log('Manual onboarding completion triggered');
      // This would need to be called with proper user context
      alert('Use the onboarding flow in the app to complete setup');
    },
    checkOnboardingStatus: () => {
      const preferences = localStorage.getItem('userPreferences');
      console.log('Current preferences:', preferences);
      return preferences;
    }
  };
}

export const useUserPreferences = () => {
  const { user } = useAuth();
  const [preferences, setPreferences] = useState<ExtendedUserPreferences | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch user preferences
  const fetchPreferences = async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      // Use AbortController for better timeout handling
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 4000); // Reduced from 5s to 4s

      const { data, error } = await supabase
        .from('user_preferences')
        .select('*')
        .eq('user_id', user.id)
        .abortSignal(controller.signal)
        .maybeSingle();

      clearTimeout(timeoutId);

      if (error) {
        throw error;
      }

      setPreferences(data as ExtendedUserPreferences);
      setError(null);
    } catch (err) {
      console.error('Error fetching user preferences:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch preferences';
      setError(errorMessage);
      
      // Set minimal default preferences on error to prevent infinite loading
      if (errorMessage.includes('timeout') || errorMessage.includes('aborted') || errorMessage.includes('network')) {
        console.warn('Using default preferences due to network issues');
        setPreferences({
          user_id: user.id,
          email_notifications: true,
          comment_notifications: true,
          rating_notifications: true,
          marketing_notifications: false,
          deactivated: false,
          last_updated_at: new Date().toISOString()
        } as ExtendedUserPreferences);
      }
    } finally {
      setLoading(false);
    }
  };

  // Update preferences
  const updatePreferences = async (updates: Partial<ExtendedUserPreferences>) => {
    if (!user) {
      throw new Error('User not authenticated');
    }

    try {
      // Only include fields that actually exist in the user_preferences table
      const validFields: any = {
        user_id: user.id,
        last_updated_at: new Date().toISOString()
      };

      // Add optional fields if they exist
      if (updates.email_notifications !== undefined) validFields.email_notifications = updates.email_notifications;
      if (updates.comment_notifications !== undefined) validFields.comment_notifications = updates.comment_notifications;
      if (updates.rating_notifications !== undefined) validFields.rating_notifications = updates.rating_notifications;
      if (updates.marketing_notifications !== undefined) validFields.marketing_notifications = updates.marketing_notifications;
      if (updates.deactivated !== undefined) validFields.deactivated = updates.deactivated;

      // Try to update first
      const { data: updateData, error: updateError } = await supabase
        .from('user_preferences')
        .update(validFields)
        .eq('user_id', user.id)
        .select()
        .single();

      if (updateError && updateError.code === 'PGRST116') {
        // No existing record, try to insert
        console.log('No existing preferences found, creating new record...');
        const { data: insertData, error: insertError } = await supabase
          .from('user_preferences')
          .insert(validFields)
          .select()
          .single();

        if (insertError) {
          console.error('Insert error:', insertError);
          throw insertError;
        }

        setPreferences(insertData as ExtendedUserPreferences);
        return insertData;
      } else if (updateError) {
        console.error('Update error:', updateError);
        throw updateError;
      }

      setPreferences(updateData as ExtendedUserPreferences);
      return updateData;
    } catch (err) {
      console.error('Full error details:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to update preferences';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  // Initialize preferences with defaults
  const initializePreferences = async () => {
    if (!user) {
      throw new Error('User not authenticated');
    }

    const defaultPreferences = {
      user_id: user.id,
      email_notifications: true,
      comment_notifications: true,
      rating_notifications: true,
      marketing_notifications: false,
      deactivated: false,
      last_updated_at: new Date().toISOString()
    };

    return await updatePreferences(defaultPreferences);
  };

  // Mark onboarding as completed (using a future extension approach)
  const completeOnboarding = async (onboardingData: {
    learning_style?: 'visual' | 'reading' | 'interactive' | 'mixed';
    preferred_difficulty?: number;
    study_time_availability?: number;
    weekly_study_goal?: number;
  }) => {
    try {
      if (!user) {
        throw new Error('User not authenticated');
      }

      // Use direct database update approach
      const { data, error } = await supabase
        .from('user_preferences')
        .update({ 
          marketing_notifications: true,
          email_notifications: true,
          comment_notifications: true,
          rating_notifications: true,
          deactivated: false,
          last_updated_at: new Date().toISOString()
        })
        .eq('user_id', user.id)
        .select()
        .single();
        
      if (error) {
        console.error('Failed to update preferences:', error);
        throw new Error(`Failed to update preferences: ${error.message}`);
      }

      setPreferences(data as ExtendedUserPreferences);

      // Store onboarding data in the profiles table bio field
      const onboardingPayload = {
        onboarding_completed: true,
        learning_preferences: onboardingData,
        completed_at: new Date().toISOString(),
        preferences: {
          marketing_notifications: true,
          email_notifications: true,
          comment_notifications: true,
          rating_notifications: true,
          deactivated: false
        }
      };

      const { error: bioError } = await supabase
        .from('profiles')
        .update({
          bio: JSON.stringify(onboardingPayload)
        })
        .eq('id', user.id);

      if (bioError) {
        console.warn('Could not save onboarding data to profile:', bioError);
        // Don't fail the whole process if profile update fails
      }

      // Also update user metadata if possible (this helps with immediate state changes)
      try {
        await supabase.auth.updateUser({
          data: { 
            ...user.user_metadata,
            onboarding_completed: true,
            has_visited_dashboard: true
          }
        });
      } catch (metaError) {
        console.warn('Could not update user metadata:', metaError);
        // Don't fail the whole process if metadata update fails
      }

      return true;
    } catch (err) {
      console.error('Error completing onboarding:', err);
      throw err;
    }
  };

  // Check if user has completed onboarding
  const hasCompletedOnboarding = () => {
    // First check if we have any preferences at all
    if (!preferences) {
      return false;
    }

    // Check if the user has a profile with onboarding data
    // This is a more reliable way to check onboarding completion
    if (user?.user_metadata?.onboarding_completed) {
      return true;
    }

    // Fallback: if user has set any learning preferences, consider onboarding done
    // This handles users who may have completed onboarding before
    const hasLearningPrefs = preferences.learning_style || 
                           preferences.preferred_difficulty || 
                           preferences.study_time_availability ||
                           preferences.weekly_study_goal;
    
    if (hasLearningPrefs) {
      return true;
    }

    // Also check if they have explicitly set notification preferences
    // (users who engaged with the platform)
    const hasEngaged = preferences.email_notifications !== null || 
                      preferences.comment_notifications !== null;
    
    return hasEngaged;
  };

  // Get learning style preference
  const getLearningStyle = (): 'visual' | 'reading' | 'interactive' | 'mixed' => {
    const style = preferences?.learning_style;
    if (style === 'visual' || style === 'reading' || style === 'interactive' || style === 'mixed') {
      return style;
    }
    return 'mixed';
  };

  // Get preferred difficulty
  const getPreferredDifficulty = (): number => {
    return preferences?.preferred_difficulty || 2;
  };

  // Get study time availability (minutes)
  const getStudyTimeAvailability = (): number => {
    return preferences?.study_time_availability || 30;
  };

  // Get weekly study goal (hours)
  const getWeeklyStudyGoal = (): number => {
    return preferences?.weekly_study_goal || 5;
  };

  useEffect(() => {
    if (user?.id) {
      // Shorter timeout to prevent infinite loading (reduced from 8s to 2s)
      const loadingTimeout = setTimeout(() => {
        console.warn('User preferences loading timeout - using defaults');
        setLoading(false);
        setError(null); // Don't show error to user for timeout
        // Set minimal preferences to prevent infinite loops
        setPreferences({
          user_id: user.id,
          email_notifications: true,
          comment_notifications: true,
          rating_notifications: true,
          marketing_notifications: false,
          deactivated: false,
          last_updated_at: new Date().toISOString()
        } as ExtendedUserPreferences);
      }, 2000); // Reduced from 8s to 2s for faster UX

      // Start fetching preferences immediately
      fetchPreferences().finally(() => {
        clearTimeout(loadingTimeout);
      });
      
      return () => {
        clearTimeout(loadingTimeout);
      };
    } else {
      setLoading(false);
      setPreferences(null);
    }
  }, [user?.id]);

  return {
    preferences,
    loading,
    error,
    updatePreferences,
    initializePreferences,
    completeOnboarding,
    hasCompletedOnboarding,
    getLearningStyle,
    getPreferredDifficulty,
    getStudyTimeAvailability,
    getWeeklyStudyGoal,
    refetch: fetchPreferences
  };
}; 